/**
 * Every user-visible date and time in the product is formatted here, and nowhere else.
 *
 * The reason is a bug that shipped: a visit stored as 2026-08-20 14:30 America/New_York was
 * rendered by `toLocaleTimeString()` with no zone. On a laptop in New York that prints
 * "2:30 PM". On a UTC machine — a server, a CI runner, a homeowner whose phone is set wrong,
 * anyone travelling — it prints "6:30 PM". Both are correct renderings of the same instant,
 * which is what makes it dangerous: nothing looks broken until somebody is not at the house.
 *
 * So the zone is a required argument in every function below. There is no default, because a
 * default is exactly the thing that was wrong: it looked right wherever it was written. The
 * value comes from the server (`booking.timezone`, `slots.timezone`, the operator's
 * availability settings) which is the only place that knows what zone the visit is *in*.
 *
 * `eslint.config.js` blocks `toLocaleString`/`toLocaleDateString`/`toLocaleTimeString`/
 * `Intl.DateTimeFormat` everywhere except this file, so the next screen cannot reintroduce it.
 */

/** The business runs in one zone (10 §3). Used only as the server's own fallback. */
export const BUSINESS_TIMEZONE = 'America/New_York'

/** "Thursday, August 20, 2026" — the headline date on a confirmation. */
export function longDay(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  })
}

/** "2:30 – 3:30 PM EDT" — an appointment window, always carrying its zone. */
export function timeWindow(
  startIso: string | Date,
  endIso: string | Date,
  timeZone: string,
): string {
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone }
  const start = new Date(startIso).toLocaleTimeString('en-US', opts)
  const end = new Date(endIso).toLocaleTimeString('en-US', { ...opts, timeZoneName: 'short' })
  return `${start} – ${end}`
}

export function shortDateTime(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  })
}

/** "Thu, Aug 20" */
export function shortDate(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  })
}

/** "Thursday" / "August 20" — the two halves the slot picker groups by. */
export function dayHeading(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone,
  })
}

/** "2:30 PM" */
export function clockTime(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  })
}

/**
 * The calendar day an instant falls on, *in a given zone*, as `YYYY-MM-DD`.
 *
 * Grouping slots by a rendered label ("Thursday, August 20") works until two months collide or
 * the label format changes; a sortable key does not. `en-CA` is the locale whose numeric date
 * format is already ISO order, which is why it appears here rather than `en-US`.
 */
export function dayKey(iso: string | Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso))
}

/** "Thu" */
export function weekdayShort(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', timeZone })
}

/** "20" — the day of the month, without a leading zero. */
export function dayOfMonth(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', timeZone })
}

/** "August 2026" — the heading over a run of days. */
export function monthLabel(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone })
}

/**
 * The hour of the day (0–23) an instant falls on in a given zone.
 *
 * Used only to split times into morning / afternoon / evening, which is how people actually
 * choose one. `hourCycle: 'h23'` keeps midnight as `00` rather than `24`.
 */
export function hourIn(iso: string | Date, timeZone: string): number {
  const hour = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hourCycle: 'h23',
  }).format(new Date(iso))
  return Number(hour)
}

/** "Today" / "Tomorrow" / "" — a relative name for a day, when there is a useful one. */
export function relativeDayName(iso: string | Date, timeZone: string): string {
  const target = dayKey(iso, timeZone)
  const now = new Date()
  if (target === dayKey(now, timeZone)) return 'Today'
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  if (target === dayKey(tomorrow, timeZone)) return 'Tomorrow'
  return ''
}

/**
 * "20 Aug" — the "as of" date on a material price (01 §7).
 *
 * Short on purpose: it sits inside a product card, next to the price, and its whole job is to
 * say *when* that price was true. Day and month is enough for a price captured within the last
 * week, which is the only kind the studio is allowed to show (06 §1).
 */
export function shortDay(iso: string | Date, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone })
}
